import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SuppliersApiService from "../../../infrastructure/api/suppliers";

const ProductManagement = () => {
  // State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
  });
  const [filters, setFilters] = useState({
    searchTerm: "",
    category: "",
    isAvailable: "",
  });

  // Dialog States
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    productId: null,
    productName: "",
    category: "",
    brand: "",
    model: "",
    specifications: "",
    unitPrice: "",
    stockQuantity: "",
    minOrderQuantity: 1,
    warrantyMonths: "",
    deliveryDays: "",
    isAvailable: true,
    description: "",
    imageUrl: "",
  });

  // Form Errors
  const [errors, setErrors] = useState({});

  const categories = [
    "پنل خورشیدی",
    "اینورتر",
    "باتری",
    "شارژ کنترلر",
    "کابل و اتصالات",
    "سازه نگهدارنده",
    "مانیتورینگ",
    "سایر",
  ];

  useEffect(() => {
    loadProducts();
  }, [pagination.pageNumber, pagination.pageSize]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        ...pagination,
      };
      const response = await SuppliersApiService.getMyProducts(params);
      setProducts(response.data.items || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      toast.error("خطا در بارگذاری محصولات");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, pageNumber: 1 });
    loadProducts();
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditMode(true);
      setSelectedProduct(product);
      setFormData({
        productId: product.productId,
        productName: product.productName,
        category: product.category,
        brand: product.brand || "",
        model: product.model || "",
        specifications: product.specifications || "",
        unitPrice: product.unitPrice,
        stockQuantity: product.stockQuantity,
        minOrderQuantity: product.minOrderQuantity,
        warrantyMonths: product.warrantyMonths || "",
        deliveryDays: product.deliveryDays || "",
        isAvailable: product.isAvailable,
        description: product.description || "",
        imageUrl: product.imageUrl || "",
      });
    } else {
      setEditMode(false);
      setSelectedProduct(null);
      setFormData({
        productId: null,
        productName: "",
        category: "",
        brand: "",
        model: "",
        specifications: "",
        unitPrice: "",
        stockQuantity: "",
        minOrderQuantity: 1,
        warrantyMonths: "",
        deliveryDays: "",
        isAvailable: true,
        description: "",
        imageUrl: "",
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedProduct(null);
    setErrors({});
  };

  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.productName.trim()) {
      newErrors.productName = "نام محصول الزامی است";
    }

    if (!formData.category) {
      newErrors.category = "دسته‌بندی الزامی است";
    }

    if (!formData.unitPrice || parseFloat(formData.unitPrice) <= 0) {
      newErrors.unitPrice = "قیمت باید بزرگتر از صفر باشد";
    }

    if (!formData.stockQuantity || parseInt(formData.stockQuantity) < 0) {
      newErrors.stockQuantity = "موجودی نمی‌تواند منفی باشد";
    }

    if (!formData.minOrderQuantity || parseInt(formData.minOrderQuantity) < 1) {
      newErrors.minOrderQuantity = "حداقل تعداد سفارش باید حداقل 1 باشد";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("لطفاً فیلدهای الزامی را پر کنید");
      return;
    }

    setLoading(true);
    try {
      if (editMode) {
        await SuppliersApiService.updateProduct(formData);
        toast.success("محصول با موفقیت ویرایش شد");
      } else {
        await SuppliersApiService.createProduct(formData);
        toast.success("محصول با موفقیت ایجاد شد");
      }
      handleCloseDialog();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || "خطا در ذخیره محصول");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setLoading(true);
    try {
      await SuppliersApiService.deleteProduct(productToDelete.productId);
      toast.success("محصول با موفقیت حذف شد");
      setDeleteDialog(false);
      setProductToDelete(null);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || "خطا در حذف محصول");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination({
      ...pagination,
      pageNumber: newPage + 1,
    });
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({
      pageNumber: 1,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            مدیریت محصولات
          </Typography>
          <Typography variant="body1" color="text.secondary">
            ثبت و مدیریت محصولات خود
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          ثبت محصول جدید
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="جستجوی محصول"
              value={filters.searchTerm}
              onChange={(e) =>
                setFilters({ ...filters, searchTerm: e.target.value })
              }
              placeholder="نام، برند یا مدل"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>دسته‌بندی</InputLabel>
              <Select
                value={filters.category}
                label="دسته‌بندی"
                onChange={(e) =>
                  setFilters({ ...filters, category: e.target.value })
                }
              >
                <MenuItem value="">همه</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>وضعیت موجودی</InputLabel>
              <Select
                value={filters.isAvailable}
                label="وضعیت موجودی"
                onChange={(e) =>
                  setFilters({ ...filters, isAvailable: e.target.value })
                }
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="true">موجود</MenuItem>
                <MenuItem value="false">ناموجود</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" onClick={handleSearch}>
              جستجو
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Alert severity="info">محصولی یافت نشد. محصول جدید اضافه کنید.</Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>تصویر</TableCell>
                  <TableCell>نام محصول</TableCell>
                  <TableCell>دسته‌بندی</TableCell>
                  <TableCell>برند</TableCell>
                  <TableCell align="right">قیمت (ریال)</TableCell>
                  <TableCell align="center">موجودی</TableCell>
                  <TableCell align="center">وضعیت</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.productId} hover>
                    <TableCell>
                      {product.imageUrl ? (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          alt={product.productName}
                          sx={{ width: 50, height: 50, objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.200",
                          }}
                        >
                          <ImageIcon />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{product.productName}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>{product.brand || "-"}</TableCell>
                    <TableCell align="right">
                      {product.unitPrice?.toLocaleString("fa-IR")}
                    </TableCell>
                    <TableCell align="center">
                      {product.stockQuantity}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={product.isAvailable ? "موجود" : "ناموجود"}
                        color={product.isAvailable ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(product)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(product)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={pagination.pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="تعداد در صفحه:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} از ${count}`
            }
          />
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "ویرایش محصول" : "ثبت محصول جدید"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="نام محصول"
                value={formData.productName}
                onChange={(e) =>
                  handleFormChange("productName", e.target.value)
                }
                error={!!errors.productName}
                helperText={errors.productName}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.category}>
                <InputLabel>دسته‌بندی</InputLabel>
                <Select
                  value={formData.category}
                  label="دسته‌بندی"
                  onChange={(e) => handleFormChange("category", e.target.value)}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="برند"
                value={formData.brand}
                onChange={(e) => handleFormChange("brand", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="مدل"
                value={formData.model}
                onChange={(e) => handleFormChange("model", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="قیمت (ریال)"
                value={formData.unitPrice}
                onChange={(e) => handleFormChange("unitPrice", e.target.value)}
                error={!!errors.unitPrice}
                helperText={errors.unitPrice}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="موجودی انبار"
                value={formData.stockQuantity}
                onChange={(e) =>
                  handleFormChange("stockQuantity", e.target.value)
                }
                error={!!errors.stockQuantity}
                helperText={errors.stockQuantity}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="حداقل تعداد سفارش"
                value={formData.minOrderQuantity}
                onChange={(e) =>
                  handleFormChange("minOrderQuantity", e.target.value)
                }
                error={!!errors.minOrderQuantity}
                helperText={errors.minOrderQuantity}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="گارانتی (ماه)"
                value={formData.warrantyMonths}
                onChange={(e) =>
                  handleFormChange("warrantyMonths", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="مدت زمان تحویل (روز)"
                value={formData.deliveryDays}
                onChange={(e) =>
                  handleFormChange("deliveryDays", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAvailable}
                    onChange={(e) =>
                      handleFormChange("isAvailable", e.target.checked)
                    }
                  />
                }
                label="موجود است"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="مشخصات فنی (JSON)"
                value={formData.specifications}
                onChange={(e) =>
                  handleFormChange("specifications", e.target.value)
                }
                multiline
                rows={3}
                placeholder='{"capacity": "400W", "efficiency": "21%"}'
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="توضیحات"
                value={formData.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="لینک تصویر"
                value={formData.imageUrl}
                onChange={(e) => handleFormChange("imageUrl", e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>انصراف</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "ذخیره"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>تأیید حذف</DialogTitle>
        <DialogContent>
          <Typography>
            آیا از حذف محصول "{productToDelete?.productName}" اطمینان دارید؟
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>انصراف</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "حذف"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductManagement;
